import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import {
  Activity, RefreshCw, Search, User, Clock, MapPin, Monitor,
  ChevronRight, Shield, Ban, MessageSquare, Settings, X,
  Globe, Smartphone, LogIn, Lock, Edit, UserCog
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const BACKEND_URL = ((typeof window !== 'undefined' && window.location.hostname.includes('seatpilot.co.uk')) ? 'https://seatpilot.co.uk' : (process.env.REACT_APP_BACKEND_URL || 'https://seatpilot.co.uk'));
const API = `${BACKEND_URL}/api`;

// Available menu permissions
const MENU_OPTIONS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "customers", label: "Customers" },
  { key: "support_tickets", label: "Support Tickets" },
  { key: "users_roles", label: "Users & Roles" },
  { key: "invoices", label: "Invoices" },
  { key: "subscriptions", label: "Subscriptions" },
  { key: "cms", label: "Content & CMS" },
  { key: "marketing", label: "Marketing" },
  { key: "analytics", label: "Analytics" },
  { key: "settings", label: "Settings" },
  { key: "audit_logs", label: "Audit Logs" },
];

export default function BackofficeAudit() {
  const { theme } = useOutletContext();
  const isDark = theme === 'dark';

  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [contextFilter, setContextFilter] = useState("all");
  const [total, setTotal] = useState(0);

  // User Profile Modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskMessage, setTaskMessage] = useState("");
  const [taskType, setTaskType] = useState("general");

  // Access editing
  const [editingAccess, setEditingAccess] = useState(false);
  const [accessPermissions, setAccessPermissions] = useState([]);

  useEffect(() => {
    fetchAuditLog();
  }, [contextFilter]);
const fetchAuditLog = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (contextFilter !== "all") {
        params.append("context", contextFilter);
      }
      const res = await axios.get(`${API}/activity/admin/audit?${params}`, { withCredentials: true });
      setAuditLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error("Failed to fetch audit log:", error);
      // Fallback to old endpoint
      try {
        const res = await axios.get(`${API}/internal/audit-log?limit=200`, { withCredentials: true });
        setAuditLogs(res.data.logs || []);
      } catch (e) {
        console.error("Fallback also failed:", e);
      }
    } finally {
      setLoading(false);
    }
  }, [contextFilter]);

  const fetchUserProfile = async (userId) => {
    setLoadingProfile(true);
    try {
      const res = await axios.get(`${API}/activity/admin/users/${userId}`, { withCredentials: true });
      setUserProfile(res.data);
      setAccessPermissions(res.data.user?.menu_permissions || []);
      setShowUserModal(true);
    } catch (error) {
      console.error("Failed to fetch user profile:", error);
      toast.error("Failed to load user profile");
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleUserClick = (userId) => {
    if (!userId) return;
    setSelectedUser(userId);
    fetchUserProfile(userId);
  };

  const handleBlockUser = async () => {
    if (!userProfile?.user) return;
    const isBlocked = userProfile.user.is_blocked;
    
    try {
      await axios.put(`${API}/activity/admin/users/${userProfile.user.user_id}/access`, {
        is_blocked: !isBlocked,
        block_reason: isBlocked ? null : "Blocked by admin"
      }, { withCredentials: true });
      
      toast.success(isBlocked ? "User unblocked" : "User blocked");
      fetchUserProfile(userProfile.user.user_id);
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const handleUpdateAccess = async () => {
    if (!userProfile?.user) return;
    
    try {
      await axios.put(`${API}/activity/admin/users/${userProfile.user.user_id}/access`, {
        menu_permissions: accessPermissions
      }, { withCredentials: true });
      
      toast.success("Access permissions updated");
      setEditingAccess(false);
      fetchUserProfile(userProfile.user.user_id);
    } catch (error) {
      toast.error("Failed to update access");
    }
  };

  const handleSendTask = async () => {
    if (!userProfile?.user || !taskMessage.trim()) return;
    
    try {
      await axios.post(`${API}/activity/admin/users/${userProfile.user.user_id}/task`, {
        message: taskMessage,
        task_type: taskType,
        priority: "medium"
      }, { withCredentials: true });
      
      toast.success("Task sent to user");
      setShowTaskModal(false);
      setTaskMessage("");
    } catch (error) {
      toast.error("Failed to send task");
    }
  };

  const togglePermission = (key) => {
    setAccessPermissions(prev => 
      prev.includes(key) 
        ? prev.filter(p => p !== key) 
        : [...prev, key]
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-GB', {
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionColor = (action) => {
    const actionStr = action || '';
    if (actionStr.includes('create') || actionStr.includes('add')) return 'bg-green-100 text-green-700';
    if (actionStr.includes('delete') || actionStr.includes('remove')) return 'bg-red-100 text-red-700';
    if (actionStr.includes('update') || actionStr.includes('edit')) return 'bg-blue-100 text-blue-700';
    if (actionStr.includes('block')) return 'bg-orange-100 text-orange-700';
    if (actionStr.includes('login') || actionStr.includes('auth')) return 'bg-purple-100 text-purple-700';
    if (actionStr.includes('session')) return 'bg-indigo-100 text-indigo-700';
    return 'bg-gray-100 text-gray-700';
  };

  const filteredLogs = auditLogs.filter(log => {
    const searchLower = searchTerm.toLowerCase();
    const action = (log.action || '').toLowerCase();
    const userId = (typeof log.user_id === 'string' ? log.user_id : '').toLowerCase();
    const details = (typeof log.details === 'string' ? log.details : '').toLowerCase();
    const userEmail = (log.user?.email || '').toLowerCase();
    return action.includes(searchLower) || 
           userId.includes(searchLower) || 
           details.includes(searchLower) ||
           userEmail.includes(searchLower);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Audit Logs</h1>
          <p className={`mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            Track all user activity across the platform ({total} total entries)
          </p>
        </div>
        <Button onClick={fetchAuditLog} variant="outline" size="sm" className="gap-2">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className={`p-4 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <div className="flex items-center gap-2 flex-1 w-full">
            <Search className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
            <Input
              placeholder="Search by action, user, or details..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`flex-1 ${isDark ? 'bg-slate-700 border-slate-600' : ''}`}
            />
          </div>
          <Select value={contextFilter} onValueChange={setContextFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filter by context" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Activity</SelectItem>
              <SelectItem value="user">User Activity</SelectItem>
              <SelectItem value="admin">Admin Activity</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Audit Log */}
      <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Activity Log</h3>
          <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            {filteredLogs.length} entries
          </span>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" />
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="text-center py-12">
            <Activity className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
            <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No audit logs found</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {filteredLogs.map((log, index) => (
              <div 
                key={log.log_id || index} 
                className={`p-4 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getActionColor(log.action)}`}>
                        {(log.action || 'unknown')?.replace(/_/g, ' ')}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs ${log.context === 'admin' ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'}`}>
                        {log.context || 'user'}
                      </span>
                    </div>
                    <p className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {typeof log.details === 'string' ? log.details : (log.action || 'unknown')?.replace(/_/g, ' ')}
                    </p>
                    
                    {/* User info - clickable */}
                    <div className="flex items-center gap-4 mt-2 flex-wrap">
                      <button 
                        onClick={() => handleUserClick(log.user_id)}
                        className={`flex items-center gap-1 text-xs hover:underline ${isDark ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-700'}`}
                      >
                        <User className="w-3 h-3" />
                        {log.user?.email || log.user?.name || log.user_id || 'System'}
                        <ChevronRight className="w-3 h-3" />
                      </button>
                      <span className={`flex items-center gap-1 text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        <Clock className="w-3 h-3" />
                        {formatDate(log.timestamp)}
                      </span>
                    </div>

                    {/* Location & Device info */}
                    {(log.location || log.device || log.ip_address) && (
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        {log.ip_address && (
                          <span className={`flex items-center gap-1 text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                            <Globe className="w-3 h-3" />
                            {log.ip_address}
                          </span>
                        )}
                        {log.location?.city && (
                          <span className={`flex items-center gap-1 text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                            <MapPin className="w-3 h-3" />
                            {log.location.city}, {log.location.country}
                          </span>
                        )}
                        {log.device?.browser && (
                          <span className={`flex items-center gap-1 text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                            {log.device.is_mobile ? <Smartphone className="w-3 h-3" /> : <Monitor className="w-3 h-3" />}
                            {log.device.browser}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* User Profile Modal */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              User Profile
            </DialogTitle>
          </DialogHeader>
          
          {loadingProfile ? (
            <div className="text-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" />
            </div>
          ) : userProfile?.user ? (
            <div className="space-y-6">
              {/* User Info */}
              <div className="flex items-start gap-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-bold ${userProfile.user.is_blocked ? 'bg-red-500' : 'bg-violet-600'}`}>
                  {userProfile.user.name?.charAt(0) || userProfile.user.email?.charAt(0) || '?'}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">{userProfile.user.name || 'Unknown'}</h3>
                  <p className="text-sm text-gray-500">{userProfile.user.email}</p>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded text-xs ${userProfile.user.role === 'super_admin' ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'}`}>
                      {userProfile.user.role || 'user'}
                    </span>
                    {userProfile.user.is_blocked && (
                      <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">Blocked</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowTaskModal(true)}
                  >
                    <MessageSquare className="w-4 h-4 mr-1" />
                    Send Task
                  </Button>
                  <Button 
                    variant={userProfile.user.is_blocked ? "default" : "destructive"}
                    size="sm"
                    onClick={handleBlockUser}
                  >
                    {userProfile.user.is_blocked ? (
                      <><Shield className="w-4 h-4 mr-1" /> Unblock</>
                    ) : (
                      <><Ban className="w-4 h-4 mr-1" /> Block</>
                    )}
                  </Button>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">{userProfile.stats?.total_activities || 0}</p>
                  <p className="text-xs text-gray-500">Total Activities</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">{userProfile.stats?.total_tickets || 0}</p>
                  <p className="text-xs text-gray-500">Support Tickets</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">{userProfile.stats?.assigned_tickets || 0}</p>
                  <p className="text-xs text-gray-500">Assigned Tickets</p>
                </div>
              </div>

              {/* Menu Access Permissions */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium flex items-center gap-2">
                    <UserCog className="w-4 h-4" />
                    Menu Access Permissions
                  </h4>
                  {editingAccess ? (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setEditingAccess(false)}>Cancel</Button>
                      <Button size="sm" onClick={handleUpdateAccess}>Save</Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => setEditingAccess(true)}>
                      <Edit className="w-3 h-3 mr-1" /> Edit
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {MENU_OPTIONS.map(option => (
                    <label key={option.key} className={`flex items-center gap-2 p-2 rounded ${editingAccess ? 'hover:bg-gray-100 cursor-pointer' : ''}`}>
                      <Checkbox
                        checked={accessPermissions.includes(option.key)}
                        onCheckedChange={() => editingAccess && togglePermission(option.key)}
                        disabled={!editingAccess}
                      />
                      <span className="text-sm">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Login History */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <LogIn className="w-4 h-4" />
                  Recent Login History
                </h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {userProfile.login_history?.length > 0 ? (
                    userProfile.login_history.map((entry, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                        <div className="flex items-center gap-3">
                          <span className="text-gray-500">{formatDate(entry.timestamp)}</span>
                          {entry.location?.city && (
                            <span className="flex items-center gap-1 text-gray-400">
                              <MapPin className="w-3 h-3" />
                              {entry.location.city}, {entry.location.country}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {entry.device?.browser && (
                            <span className="text-xs text-gray-400">{entry.device.browser}</span>
                          )}
                          <span className="text-xs text-gray-400 font-mono">{entry.ip}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-400">No login history</p>
                  )}
                </div>
              </div>

              {/* Active Sessions */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Active Sessions ({userProfile.active_sessions?.length || 0})
                </h4>
                {userProfile.active_sessions?.length > 0 ? (
                  <div className="space-y-2">
                    {userProfile.active_sessions.map((session, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                        <span className="text-gray-500">{formatDate(session.created_at)}</span>
                        <span className="text-xs text-gray-400">Expires: {formatDate(session.expires_at)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">No active sessions</p>
                )}
              </div>

              {/* Recent Activity */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Activity className="w-4 h-4" />
                  Recent Activity
                </h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {userProfile.recent_activity?.length > 0 ? (
                    userProfile.recent_activity.map((act, idx) => (
                      <div key={idx} className="p-2 bg-gray-50 rounded">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${getActionColor(act.action)}`}>
                            {act.action?.replace(/_/g, ' ')}
                          </span>
                          <span className="text-xs text-gray-400">{formatDate(act.timestamp)}</span>
                        </div>
                        {act.details && <p className="text-xs text-gray-600 mt-1">{act.details}</p>}
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-400">No recent activity</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-500">User not found</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Task Modal */}
      <Dialog open={showTaskModal} onOpenChange={setShowTaskModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Task to User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Task Type</label>
              <Select value={taskType} onValueChange={setTaskType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General Task</SelectItem>
                  <SelectItem value="urgent">Urgent Task</SelectItem>
                  <SelectItem value="follow_up">Follow Up</SelectItem>
                  <SelectItem value="review">Review Required</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Message</label>
              <textarea
                className="w-full p-3 border rounded-lg"
                rows={4}
                placeholder="Enter task description or message..."
                value={taskMessage}
                onChange={(e) => setTaskMessage(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowTaskModal(false)}>Cancel</Button>
              <Button onClick={handleSendTask} disabled={!taskMessage.trim()}>Send Task</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
